import { FC, useContext, useState } from "react";
import { useParams } from "react-router-dom";

import { Button, CircularProgress } from "@mui/material";

import StatusCheckedIcon from "@/assets/svg/interactions/icon-status-checked.svg?react";
import StatusUncheckedIcon from "@/assets/svg/interactions/icon-status-unchecked.svg?react";

import { IChecklistStep } from "@/components/Procedures/Checklist/utils/Types";
import { RemoveStepCheckedModal } from "@/components/Procedures/RemoveStepCheckedModal";

import { ProcedureNodeStep } from "@/model/Procedure/ProcedureNodeStep";
import { ProcedureNodeStepStatusTypes } from "@/model/Procedure/ProcedureNodeStepStatus";
import { ProcedureNodeStepStatus } from "@/model/Procedure/ProcedureNodeSteps";
import { UpdateProcedureDetailStepStatusRequest } from "@/model/Procedure/UpdateProcedureDetailStepStatusRequest";
import { UserContext } from "@/model/User/UserContext";
import {
	UserCompanyConfigurationKeys as ConfigKeys,
	GetUserCompanyConfigurationToggle,
} from "@/model/UserCompanyConfiguration/UserCompanyConfiguration";
import { UserCompanyConfigurationContext } from "@/model/UserCompanyConfiguration/UserCompanyConfigurationContext";

import {
	checkAdminAccess,
	checkGroupAccess,
} from "@/service/ApplicationQueries";
import { UpdateProcedureDetailStepState } from "@/service/ProcedureMutations";
import { useFetchActiveChecklistStatus } from "@/service/ProcedureQueries";
import { RouteParams } from "@/service/Utilities";
import { CheckpointType } from "@/components/Procedures/Types/ManualChecklistTypes";

export const ChecklistStatus: FC<IChecklistStep> = (props) => {
	const { id } = useParams<RouteParams>();
	const { step } = props;
	const [disabled, setDisabled] = useState(false);
	const [showRemoveCheckedStepModal, setShowRemoveCheckedStepModal] =
		useState(false);
	const updateProcedureDetailStepState = UpdateProcedureDetailStepState();
	const { isLoading, data: stepStatuses } = useFetchActiveChecklistStatus(
		props.activeSchemeId,
	);

	const user = useContext(UserContext);

	const CloseRemoveStepCheckedModal = () => {
		setShowRemoveCheckedStepModal(false);
	};

	const userCompanyConfiguration = useContext(UserCompanyConfigurationContext);

	const forceComment = () => {
		return GetUserCompanyConfigurationToggle(
			ConfigKeys.ForceComments,
			userCompanyConfiguration,
		);
	};

	const Save = async (readonly: boolean) => {
		const stepStatus = stepStatuses?.find((x) => x.Id === props.step.Id)!;
		if (readonly || props.step.Readonly || stepStatus.Disabled) {
			return;
		}

		let postValueToServer = false;
		let status: ProcedureNodeStepStatusTypes = 0;

		if (!stepStatus.Status) {
			status = 1;
			postValueToServer = true;
		} else if (stepStatus.Status) {
			if (forceComment()) {
				setShowRemoveCheckedStepModal(true);
			} else {
				postValueToServer = true;
			}
		}

		if (postValueToServer) {
			const request: UpdateProcedureDetailStepStatusRequest = {
				ArchiveType: "Status",
				StepId: props.step.Id,
				ProcedureId: parseInt(id),
				SchemeId: props.activeSchemeId,
				Status: status,
				SavedTagValue: undefined,
				Metadata: { ...props.step, Status: status },
			};

			await updateProcedureDetailStepState(request);
		}
	};

	if (
		props.step?.AccessLevel === "Locked" ||
		props.step.CheckpointType === CheckpointType.SATOSLive ||
		props.step.CheckpointType === CheckpointType.Info
	) {
		return <></>;
	}

	const getStepStatus = (
		step: ProcedureNodeStep,
		statuses: ProcedureNodeStepStatus[] | undefined,
	): ProcedureNodeStepStatus | undefined => {
		if (step && Array.isArray(statuses)) {
			// Ensure statuses is an array before calling find
			const intstatus = statuses.find((status) => status.Id === step.Id);

			if (intstatus) {
				intstatus.readonly = false;
			}

			if (intstatus && props.step?.GroupId > 0 && user) {
				// Check group and admin access
				if (
					!checkGroupAccess(user.Groups, [props.step?.GroupId]) &&
					!checkAdminAccess(user)
				) {
					intstatus.readonly = true;
					if (!intstatus.Status) {
						intstatus.Disabled = true;
					}
				}
			}
			return intstatus;
		}
		return undefined; // Return undefined when statuses is not an array or not available
	};

	const status = getStepStatus(props.step, stepStatuses);

	if (isLoading) {
		return <CircularProgress />;
	}

	if (status === undefined || status.Disabled || (step.CheckpointType === CheckpointType.ManualMeasurement && !status.Status)) {
		return (
			<Button
				variant="contained"
				color="error"
				disabled={true}
				type="button"
				sx={{
					padding: 1,
					minWidth: "40px",
					margin: 1,
				}}
			>
				<StatusUncheckedIcon className="svg-icon svg-icon-white svg-icon--status" />
			</Button>
		);
	} else if (status.Status) {
		return (
			<>
				<Button
					variant="contained"
					color="success"
					disabled={props.step.IsSkipped}
					onClick={() => Save(status.readonly)}
					type="button"
					sx={{
						padding: 1,
						minWidth: "40px",
						margin: 1,
					}}
				>
					<StatusCheckedIcon className="svg-icon svg-icon-white svg-icon--status" />
				</Button>
				<RemoveStepCheckedModal
					activeSchemeId={props.activeSchemeId}
					show={showRemoveCheckedStepModal}
					step={props.step}
					CloseModal={CloseRemoveStepCheckedModal}
				/>
			</>
		);
	} else {
		return (
			<Button
				variant="contained"
				color="error"
				disabled={props.step.IsSkipped}
				onClick={() => Save(status.readonly)}
				type="button"
				sx={{
					padding: 1,
					minWidth: "40px",
					margin: 1,
				}}
			>
				<StatusUncheckedIcon className="svg-icon svg-icon-white svg-icon--status" />
			</Button>
		);
	}
};
